import React, { useEffect, useState } from 'react';
import { useIntl} from 'react-intl';

import { useAuthenticationDataContext } from '../../Helpers/AuthenticationDataProvider';
import { useAuthorizationDataContext } from '../../Helpers/AuthorizationDataProvider';
import useFetchWrapper, { HttpResponse } from '../../Helpers/useFetchWrapper';
import useScoreHelper, { Score } from '../../Helpers/useScoreHelper';

import {Action, FormPopupField} from "../../components/Popup/FormPopup.model";
import { CurrentUserResponse, UpgradeProfileRequest, } from './EditCurrentProfile.model';
import { useHistory } from 'react-router-dom';
import {useIntlWrapperContext} from "../../Helpers/IntlWrapperProvider";
import useObjectHelper from "../../Helpers/useObjectHelper";
import {mentoringScopes} from "../../Helpers/StaticData";
import {WORDPRESS_DOMAIN} from "../../Helpers/EnvVars";

export const useEditCurrentProfile = () => {
	const [averageScore, setAverageScore] = useState<Score | null>(null);
	const [currentUser, setCurrentUser] = useState<CurrentUserResponse>();
	const [activeTab, setActiveTab] = useState('1');
	const [editImage, setEditImage] = useState<boolean>(false);
	const [showUpgradeProfileForm, setShowUpgradeProfileForm] = useState(false);

	const { arrToOptionList } = useObjectHelper();
	const intl = useIntl();

	const upgradeProfileFormFields: FormPopupField[] = [
		{
			id: 'mentoringScopes',
			layout: 'w100',
			options: { optionList: arrToOptionList(mentoringScopes) },
			rules: { required: true },
			type: 'selectMultiple',
		},
		{
			id: 'motivation',
			layout: 'w100',
			rules: { required: true },
			type: 'textArea',
		},
		{
			id: 'consentCodeOfConduct',
			label: React.createElement("span", null, intl.formatMessage({id: 'formLabels.consent'}), React.createElement("a", {href: `${WORDPRESS_DOMAIN}/code-of-conduct`, rel: "noreferrer" ,target: "_blank"}, ` ${intl.formatMessage({id: 'formLabels.codeOfConduct'})}`)),
			layout: 'w100',
			rules: { required: true },
			type: 'checkbox',
		},
	];

	const { get, post } = useFetchWrapper();
	const { userId } = useAuthenticationDataContext();
	const { getScoreByAverageRating } = useScoreHelper();
	const history = useHistory();

	const { getI18nLanguageName } = useIntlWrapperContext();
	const { isAuthorized } = useAuthorizationDataContext();

	const upgradeProfileFormActions: Action[] = [{ onClick: () => { setShowUpgradeProfileForm(false) }, title: 'cancel', type: 'default' }, {
		isSubmitButton: true,
		title: 'sendRequest',
		type: 'primary'
	}];

	useEffect(() => {
		getCurrentUser();
	}, []);
	
	useEffect(() => {
		currentUser?.role === 'Mentor' && getUserRating();
	}, [currentUser]);

	const onChangeTab = (activeKey: string) => {
		setActiveTab(activeKey);
	};

	const getCurrentUser = async () => {
		const response: HttpResponse<CurrentUserResponse> = await get(
			`/user/profile/${userId}`
		);
		if (response.parsedBody) {
			const user = response.parsedBody;
			setCurrentUser({ ...user });
		}
	};

	const formatDate = (date: string) => {
		return intl.formatDate(new Date(date), { month: 'long', year: 'numeric' });
	};

	const getUserRating = async () => {
		const response: HttpResponse<any> = await get(
			`/review/rating/${userId}?page=1&size=5`
		);

		if (response.parsedBody) {
			const { averageScore } = response.parsedBody;
			setAverageScore(averageScore && getScoreByAverageRating(averageScore));
		}
	};

	const navigateTo = (path: string, state?: any, search?: string) => {
		history.push({ pathname: path, search, state });
	};
	
	const toggleImagePopup = () => {
		setEditImage(prevState => !prevState)
	}

	const toggleUpgradeProfileForm = () => {
		setShowUpgradeProfileForm((currentValue: boolean) => !currentValue);
	}

	const upgradeProfile = async (data: UpgradeProfileRequest) => {
		await post(`/user/upgrade-role`, {
			introduction: data.motivation,
			mentoringScopes: data.mentoringScopes,
		});

		currentUser && setCurrentUser({
			...currentUser,
			canRequestUpgradeRole: false
		});
	} 

	return {
		activeTab,
		editImage,
		formatDate,
		getI18nLanguageName,
		isAuthorized,
		navigateTo,
		onChangeTab,
		setCurrentUser,
		state: {
			averageScore,
			currentUser,
			showUpgradeProfileForm,
		},
		toggleImagePopup,
		toggleUpgradeProfileForm,
		upgradeProfile,
		upgradeProfileFormActions,
		upgradeProfileFormFields
	};
};
export default useEditCurrentProfile;